/**
 * Centralized application state management
 * Manages navigation, active views, and ensures consistent state across components
 */

export type ViewType = 'login' | 'landing' | 'report-selection' | 'report-parameters' | 'export-data';
export type TabType = 'my-reports' | 'create-report' | 'export-data';

interface UserData {
    email?: string;
    organizationId?: string;
    organizationName?: string;
    [key: string]: any;
}

interface AppState {
    currentView: ViewType;
    activeTab: TabType | null;
    isHeaderInitialized: boolean;
    selectedReport: string | null;
    user: UserData | null;
    organizationName: string | null;
}

class StateManager {
    private state: AppState = {
        currentView: 'login',
        activeTab: null,
        isHeaderInitialized: false,
        selectedReport: null,
        user: null,
        organizationName: null
    };

    private listeners: Array<(state: AppState) => void> = [];

    private orgCache: { list: any[]; fetchedAt: number } | null = null;
    private readonly ORG_CACHE_TTL = 5 * 60 * 1000;
    
    getState(): AppState {
        return {...this.state};
    }
    
    setState(updates: Partial<AppState>): void {
        this.state = {...this.state, ...updates};
        this.notifyListeners();
    }
    
    subscribe(listener: (state: AppState) => void): () => void {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }
    
    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this.getState()));
    }
    
    setView(view: ViewType): void {
        this.setState({currentView: view});
    }
    
    setActiveTab(tab: TabType | null): void {
        this.setState({activeTab: tab});
        this.updateTabUI(tab);
    }
    
    private updateTabUI(activeTab: TabType | null): void {
        // Create has no nav button of its own any more — it is reached from the New
        // button on the Reports tab — so the create flow keeps Reports lit rather than
        // leaving the nav bar with nothing selected.
        const navButtonFor: Record<TabType, string> = {
            'my-reports':    'nav-my-reports',
            'create-report': 'nav-my-reports',
            'export-data':   'nav-import'
        };
        const selectedId = activeTab ? navButtonFor[activeTab] : null;

        ['nav-my-reports', 'nav-import'].forEach(id => {
            document.getElementById(id)?.classList.toggle('selected', id === selectedId);
        });
    }
    
    markHeaderInitialized(): void {
        this.setState({isHeaderInitialized: true});
    }

    async getCachedOrgs(fetchFn: () => Promise<any[]>): Promise<any[]> {
        const now = Date.now();
        if (this.orgCache && (now - this.orgCache.fetchedAt) < this.ORG_CACHE_TTL) {
            return this.orgCache.list;
        }
        const list = await fetchFn();
        this.orgCache = { list, fetchedAt: now };
        return list;
    }

    clearOrgCache(): void {
        this.orgCache = null;
    }

    reset(): void {
        this.state = {
            currentView: 'login',
            activeTab: null,
            isHeaderInitialized: false,
            selectedReport: null,
            user: null,
            organizationName: null
        };
        this.orgCache = null;
        this.notifyListeners();
    }
}

export const stateManager = new StateManager();
